import React, { useEffect, useState } from 'react'
import { checkDeviceId } from '../../actions/contentActions'
import FocusableButtonTS from '../../components/FocusableButton/FocusableButtonTS'
import { useLocation, useNavigate } from 'react-router'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../shared/providers/KeyDownProvider'
import qrCode from '../../assets/img/pbsActivateURL.png'
import tts from '../../lib/TTS'
import {
  activateTemporarySetting,
  authCodeRequest,
  TEMPORAL_SETTINGS_AVAILABLE,
} from '../../lib/utils'
import { useAppSelector } from '../../hooks/useStore'
import {
  ComcastErrorType,
  platformStoreDelete,
  triggerSentryError,
} from '../../../../shared/helpers'
import {
  DEVICE_KEY,
  EACH_FIFTEEN_SECONDS,
  EACH_FIVE_SECONDS,
  getActivationRedirectLink,
  setTimer,
  TWELVE_ATTEMPTS,
  TWO_HUNDRED_FORTY_ATTEMPTS,
} from './utils'
import { useActivationCode } from './useActivationCode'
import VppaModal from '../../components/VppaModal'

// TODO: remove this when whole app is migrated to react spatial navigation
declare global {
  interface Window {
    focusedComponent: {
      handleBySpatialNavigation?: boolean
    }
  }
}

const ActivationCodeScreen = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [authCode, setAuthCode] = useState<string>('')
  const deviceId = useAppSelector((state) => state.device.deviceId)
  const app = deviceId.split('-')[1]

  const {
    timerResets,
    pollActivation,
    vppaModalVisibility,
    setVppaModalVisibility,
    navigateHome,
  } = useActivationCode()

  useKeyDown(KeyTypeEnum.Back, () => {
    if (!vppaModalVisibility) {
      navigate(-1)
    }
  })

  // Workaround to allow new focus components to work with spatial navigation and old components work with focusable HoC
  // TODO: remove this when whole app is migrated to react spatial navigation
  useEffect(() => {
    window.focusedComponent = {}
    window.focusedComponent.handleBySpatialNavigation = true
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
    }
  }, [])

  useEffect(() => {
    navigateHome()
    timerResets.current.push(
      setTimer(
        pollActivation,
        EACH_FIVE_SECONDS,
        TWELVE_ATTEMPTS,
        lowerPriorityActivationRetries
      )
    )
  }, [])

  useEffect(() => {
    if (deviceId && app) {
      authCodeRequest(deviceId, app)
        .then((resp) => {
          setAuthCode(resp.authCode)
        })
        .catch((err) => {
          triggerSentryError('authCodeRequest', 'ActivationCodeScreen.js', {
            errorMessage: err.message ?? 'Undefined error message (default)',
            deviceId,
            app,
            errorLocation: 'ActivationCodeScreen - Generating Code',
            errorType: ComcastErrorType.restriction,
          })
        })
    }
  }, [deviceId, app])

  useEffect(() => {
    if (authCode) {
      tts.stopRead()
      tts.addTextToRead(`To sign in this device. Step 1. From a mobile device or computer, visit: pbs.org/tv.
      or point your phone or tablet camera at the QR code below.
      Step 2. Enter code: ${authCode
        .split('')
        .join(
          ' , '
        )}. Once you sign in, this screen will automatically update within 30 seconds. The code again is: ${authCode
        .split('')
        .join(
          ' , '
        )}. If you have any issues activating with the code listed above, please use the button below to get a new code ..... Focused on Get New Code Button
      }`)
    }
    return () => tts.stopRead()
  }, [authCode])

  const finishRetries = () => {
    //polling completed, error code 500005
    //expire code,return user to activation screen
    platformStoreDelete(DEVICE_KEY)
    checkDeviceId()
    //return user to activation screen
    navigate(getActivationRedirectLink(location.search))
  }

  const lowerPriorityActivationRetries = () =>
    timerResets.current.push(
      setTimer(
        pollActivation,
        EACH_FIFTEEN_SECONDS,
        TWO_HUNDRED_FORTY_ATTEMPTS,
        finishRetries
      )
    )

  const newCodeHandler = () => {
    tts.pushTextToRead('Selected Get New Code Button')
    platformStoreDelete(DEVICE_KEY)
    checkDeviceId()
  }

  const handleCloseVppaModal = (args: {
    accepted: boolean
    declined?: boolean
  }) => {
    if (args.accepted) {
      try {
        activateTemporarySetting(TEMPORAL_SETTINGS_AVAILABLE.VPPA)
        navigateHome()
      } catch (error) {
        triggerSentryError('registerUser', 'ActivationCodeScreen.js', {
          errorMessage: error.message ?? 'Undefined error message (default)',
          errorLocation: 'ActivationCodeScreen - acceptVppa',
          errorType: ComcastErrorType.restriction,
        })
      }
    } else if (!args.accepted) {
      if (args.declined) {
        navigateHome()
      } else {
        setTimeout(() => {
          setVppaModalVisibility(false)
        }, 100)
      }
    }
  }

  return (
    <>
      {vppaModalVisibility ? (
        <VppaModal onCloseModal={handleCloseVppaModal} />
      ) : (
        <div className="activation-code-screen">
          <h1 className="activation-code-screen__header">SIGN IN</h1>
          <div className="activation-code-screen__body">
            <div className="activation-code-screen__step1">
              <h2 className="activation-code-screen__step-title">Step 1</h2>
              <div className="activation-code-screen__step1-content">
                <p>From a mobile device or computer, visit</p>
                <p className="activation-code-screen__url">pbs.org/tv</p>
                <p className="activation-code-screen__or-separator">OR</p>
                <p className="activation-code-screen__qr-info">
                  point your phone or tablet camera at the QR code below
                </p>
                <img
                  alt="no-alt"
                  className="activation-code-screen__qr-code"
                  src={qrCode}
                />
              </div>
            </div>
            <div className="activation-code-screen__step2">
              <h2 className="activation-code-screen__step-title activation-code-screen__step2-title">
                Step 2
              </h2>
              <div className="activation-code-screen__step2-content">
                <p>Enter code:</p>
                <p className="activation-code-screen__auth-code">{authCode}</p>
                <p className="activation-code-screen__sign-in-info">
                  Once you sign in, this screen will automatically update within
                  30 seconds.
                </p>
                <div className="activation-code-screen__new-code">
                  <p className="activation-code-screen__new-code-info">
                    Code not working?
                  </p>
                  <FocusableButtonTS
                    className="activation-code-screen__new-code-button"
                    stealFocus={true}
                    onEnterPress={newCodeHandler}
                  >
                    Get New Code
                  </FocusableButtonTS>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ActivationCodeScreen
